import React, { useEffect, useRef } from 'react';
import classnames from 'classnames';

interface BottomSheetProps {
    isOpen: boolean;
    onClose: () => void;
    children: React.ReactNode;
    height?: string | number;
    className?: string;
}

export const BottomSheet: React.FC<BottomSheetProps> = ({
    isOpen,
    onClose,
    children,
    height = '50%',
    className
}) => {
    const sheetRef = useRef<HTMLDivElement>(null);

    // Close on escape key
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = '';
        };
    }, [isOpen, onClose]);

    // Close on backdrop click
    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div
            className="position-fixed top-0 start-0 w-100 h-100 d-flex flex-column justify-content-end"
            style={{ zIndex: 2000, backgroundColor: 'rgba(0,0,0,0.5)' }}
            onClick={handleBackdropClick}
        >
            <div
                ref={sheetRef}
                className={classnames(
                    'bg-white rounded-top shadow-lg',
                    'd-flex flex-column',
                    'animate-slide-up',
                    className
                )}
                style={{
                    height,
                    maxHeight: '90vh',
                    animation: 'slideUp 0.3s ease-out'
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Drag handle */}
                <div className="d-flex justify-content-center py-3">
                    <div
                        style={{
                            width: '40px',
                            height: '4px',
                            backgroundColor: '#dee2e6',
                            borderRadius: '2px'
                        }}
                    />
                </div>

                {/* Content */}
                <div className="flex-grow-1 overflow-auto px-4 pb-4">
                    {children}
                </div>
            </div>

            <style>{`
                @keyframes slideUp {
                    from {
                        transform: translateY(100%);
                    }
                    to {
                        transform: translateY(0);
                    }
                }
                .animate-slide-up {
                    animation: slideUp 0.3s ease-out;
                }
            `}</style>
        </div>
    );
};
